import React, { ChangeEvent, useEffect, useState } from "react";
import { useNavigate, useParams } from 'react-router-dom';

import VehicleDetails from "@components/panels/VehicleDetails";
import DropDownMenu from "@components/inputs/DropDownMenu";
import Label from "@components/inputs/text/Label";
import useServiceHook from "@hooks/useServiceHook";
import Elliptical from "@components/spinners/Elliptical/Elliptical";
import SubmitBtn from "@components/buttons/FormButtons/SubmitBtn";
import { PanelItem, IPanelItem } from "@components/panels/PanelItems";
import { IVehicle, VehicleService } from "@services/Vehicles/Vehicles";
import { UserFormStates, useUserJourneyContext } from "@context/UserJourneyContext";
import { Country } from "@utils/constants/localisation";
import { NavBar } from "@components/navigation/NavBar/NavBar";
import * as Sentry from "@sentry/react";
import breadcrumbObject from "../../SmartOpsHome/breadcrumbObject";
import WarningBox from "@components/cards/messageBox/WarningBox";
import { countrySpecificDistanceUnit } from "@utils/misc/functions";
import { CBPAllowedPlans, PlanOptions } from "@utils/planOptions";
import { OnboardingNavigationState } from "../../types/onboardingData";
import { useAuFoV2FeJourneyFlag } from "@hooks/useFeatureFlag";

const noVehicleDetails: IVehicle = {
    id: "",
    make: "",
    model: "",
    year: "",
    color: "",
    license_plate: "",
    vin: "",
    specs: {body_type: ""},
    current_meter_value: 0,
    group_ancestry: null,
    group_name: null,
    reserved_for: null
};

export default function QuotedVehicleAssignment() {
    const { country, custId } = useParams() as { country: Country; custId: string };
    const { formState, updateFormState, resetFormState } = useUserJourneyContext();
    const { carInfo, driverDetails, contractDetails, personalInfo, cbpOnboard, selectedPotential } = formState;
    const componentName = 'Vehicle assignment';
    const hubOrState = country == Country.GB ? 'hub' : 'state';

    const [allowServiceRun, setAllowServiceRun] = useState<boolean>(!!(driverDetails.plan && carInfo.hub));
    const [showOdometer, setShowOdometer] = useState<boolean>(false);
    const [odometerCheck, setOdometerCheck] = useState<boolean>(false);
    const [startingOdometer, setStartingOdometer] = useState<number>(0);

    const navigate = useNavigate();
    const [_auFoLoading, _auFoError, auFoV2FeJourneyEnabled] = useAuFoV2FeJourneyFlag();

    const [selectedVehDetails, setSelectedVehDetails] = useState<IVehicle>(
        carInfo.make
            ? {
                id: carInfo.id,
                make: carInfo.make,
                model: carInfo.model,
                year: `${carInfo.year}`,
                color: carInfo.colour,
                license_plate: carInfo.registrationPlate,
                vin: carInfo.vin,
                specs: {body_type: carInfo.bodyType},
                current_meter_value: carInfo.odometer,
                group_ancestry: null,
                group_name: null,
                reserved_for: null
            }
            : noVehicleDetails
    );

    // NEW: Call the quoted vehicles endpoint instead of available vehicles
    const [loading, error, errMessage, vehicles] = useServiceHook(
        VehicleService.getAvailableQuotedVehicles,
        [
            {
                vehicle_type: cbpOnboard.Plan,
                hub: cbpOnboard.State,
                make: cbpOnboard.Make,
                model: cbpOnboard.Model,
                year: cbpOnboard.ModelYear
            },
            Country[country]
        ],
        true,
        []
    );

    // filter out vehicles with a populated reserverd_for field in Fleetio
    const filteredVehicles: IVehicle[] = vehicles?.filter((v: IVehicle) => !v.reserved_for);

    const onSelectVeh = (event: ChangeEvent<HTMLSelectElement>) => {
        const vehicleId = event.currentTarget.value;
        const carDetails: IVehicle = filteredVehicles.filter((item: IVehicle) => item.id === vehicleId)[0];
        if (!carDetails) {
            setSelectedVehDetails(noVehicleDetails);
            resetFormState(UserFormStates.carInfo);
            setShowOdometer(false);
            return;
        }
        setSelectedVehDetails(carDetails);
        setStartingOdometer(carDetails.current_meter_value);
        updateFormState(UserFormStates.carInfo, {
            id: carDetails.id,
            make: carDetails.make,
            model: carDetails.model,
            year: Number(carDetails.year),
            colour: carDetails.color,
            registrationPlate: carDetails.license_plate,
            vin: carDetails.vin,
            bodyType: carDetails.specs.body_type,
            odometer: carDetails.current_meter_value,
        });
        setShowOdometer(true);
    };

    // Sentry tracking
    Sentry.setTag("CSR", contractDetails.contactOwner);
    Sentry.setTag("plan", driverDetails.plan);
    Sentry.setTag("hub", carInfo.hub);
    Sentry.setTag("customer name", personalInfo.firstName + ' ' + personalInfo.lastName);
    Sentry.setTag("customer email", personalInfo.email);
    Sentry.setTag("crmid", driverDetails.crmId);
    Sentry.setTag("Start Time", new Date().toString());

    useEffect(() => {
        breadcrumbObject['Vehicle assignment']['Selected Plan'] = driverDetails.plan;
        Sentry.setTag("plan", driverDetails.plan);
        breadcrumbObject['Vehicle assignment']['Hub'] = carInfo.hub;
        Sentry.setTag("hub", carInfo.hub);
        breadcrumbObject['Vehicle assignment']['Odometer'] = carInfo.odometer.toString();
    }, [driverDetails.plan, carInfo.hub, carInfo.odometer]);

    useEffect(() => {
        breadcrumbObject['Vehicle assignment']['Customer Started Journey'] = new Date();
        if (contractDetails.contactOwnerEmail === "") {
            window.location.href = '/';
        }
    }, []);

    useEffect(() => {
        carInfo.odometer < startingOdometer ? setOdometerCheck(true) : setOdometerCheck(false);
    }, [carInfo.odometer, startingOdometer]);


    // For displaying Plan & Vehicle details before selecting the vehicle so
    // that the Onboarding specialist can confirm the vehicle matches the plan
    const planVehicleDetails: IPanelItem[] = [
        {
            header: "Plan Type",
            headerTextStyleCfg: { bold: true },
            value: selectedPotential.plan,
        },
    ];
    if (
        driverDetails.plan === PlanOptions.SplendFlexiOwn
        || driverDetails.plan === PlanOptions.SplendRentToOwnUsedVehicle
    ) {
        planVehicleDetails.push(
            {
                header: "Make / Model / Year",
                headerTextStyleCfg: { bold: true },
                value: `${cbpOnboard.Make} ${cbpOnboard.Model} ${cbpOnboard.ModelYear}`,
            },
        );
    } else if (driverDetails.plan === PlanOptions.SplendFlexi) {
        planVehicleDetails.push(
            {
                header: "Make / Model",
                headerTextStyleCfg: { bold: true },
                value: `${cbpOnboard.Make} ${cbpOnboard.Model}`,
            },
        );
    }

    // Helper function to prepare onboarding data for navigation
    const prepareOnboardingData = (): OnboardingNavigationState => ({
        vehicleData: {
            vehicleId: carInfo.id,
            make: carInfo.make,
            model: carInfo.model,
            year: String(carInfo.year),
            color: carInfo.colour,
            licensePlate: carInfo.registrationPlate,
            vin: carInfo.vin,
            bodyType: carInfo.bodyType,
            odometer: carInfo.odometer,
            hub: cbpOnboard.State,
            plan: driverDetails.plan,
        },
        quoteData: {
            potentialId: selectedPotential.id,
            pricingRecordId: cbpOnboard.PricingRecordId,
            zohoContactId: cbpOnboard.ZohoContactId,
            contactOwnerId: contractDetails.contactOwnerId,
            planId: cbpOnboard.PlanId,
            pricingTemplateId: cbpOnboard.PricingTemplateId,
            termLength: cbpOnboard.TermLength,
            termReduction: cbpOnboard.TermReduction,
            weeklyDistanceAllowance: cbpOnboard.WeeklyDistanceAllowance,
            excessDistanceCost: cbpOnboard.ExcessDistanceCost,
            vehicleCondition: cbpOnboard.VehicleCondition,
            vehicleColour: cbpOnboard.vehicleColour,
            vehicleRegistration: cbpOnboard.vehicleRegistration,
            vehicleDeliveryEta: cbpOnboard.vehicleDeliveryEta,
            vehicleDriveawayPrice: cbpOnboard.vehicleDriveawayPrice,
            setupFeeIncludingGst: String(cbpOnboard.setupFeeIncludingGst),
            setupFeeExcludingGst: String(cbpOnboard.setupFeeExcludingGst),
            reducedTermLengthWeeks: cbpOnboard.ReducedTermLengthWeeks,
            lossAndDamageWaiverValue: cbpOnboard.LossAndDamageWaiverValue,
            a1TotalValue: cbpOnboard.A1TotalValue,
            a2TotalValue: cbpOnboard.A2TotalValue,
            a3TotalValue: cbpOnboard.A3TotalValue,
            totalAddonsValue: cbpOnboard.TotalAddonsValue,
            minimumTerm: cbpOnboard.MinimumTerm,
            tripCount: cbpOnboard.TripCount || "",
            replacementTyres: cbpOnboard.ReplacementTyres,
            replacementBrakePadSet: cbpOnboard.ReplacementBrakePadSet,
            replacementOrMachiningOfBrakeDiscs: cbpOnboard.ReplacementOrMachiningOfBrakeDiscs,
            quoteAddons: cbpOnboard.quoteAddons || [],
        }
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const onboardingData = prepareOnboardingData();

        // Route based on country and plan
        if (country === Country.GB) {
            // For GB, route to FlexiOwnOnboardingParent with navigation state
            if (driverDetails.plan == CBPAllowedPlans.GBFlexiOwn || driverDetails.plan == CBPAllowedPlans.PreLovedFlexiOwn) {
                navigate(`/flexi-own-onboarding-v2/${country}/${custId}`, { state: onboardingData });
            } else {
                // For other plans in GB, fall back to existing route (can be updated later)
                navigate(`/onboarding/details`);
            }
        } else if (country === Country.AU) {
            // For AU Flexi Own plans, route to V2 onboarding if the feature flag is enabled
            if (
                auFoV2FeJourneyEnabled &&
                (driverDetails.plan == CBPAllowedPlans.SplendFlexiOwn
                    || driverDetails.plan == CBPAllowedPlans.SplendRentToOwnUsedVehicle)
            ) {
                navigate(`/flexi-own-onboarding-v2/${country}/${custId}`, { state: onboardingData });
            } else {
                // Flag off, or non-FO plan: keep existing uber-proof flow
                navigate('../uber-proof', { relative: 'path' });
            }
        } else {
            // Default fallback
            navigate(`/onboarding/details`);
        }
    };

    return (
        <>
            <NavBar pageTitle={"Vehicle assignment"} backAction={undefined} />
            <form style={{ margin: '2.5vh 2.5vw' }} onSubmit={handleSubmit} >

                <div style={{ display: 'flex', flexDirection: 'column', margin: '2.5vh 0vw' }}>
                    <Label
                        text="Plan & Vehicle Details"
                        styleCfg={{ bold: true, largeFont: true }}
                    />
                    <div
                        style={{display: "flex", flexDirection: "column"}}
                        data-testid={"planVehicleDetailsPanel"}
                    >
                        {planVehicleDetails.map((itemProps, index) => (
                            <PanelItem
                                key={`planVehicleDetailsPanel-${index}`}
                                {...itemProps}
                            />
                            ))}
                    </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', margin: '2.5vh 0vw' }}>
                    <Label
                        text={'Please assign a vehicle to the customer:'}
                        styleCfg={{ largeFont: false, bold: false }}
                    />
                    {error && <WarningBox message={errMessage || "Error loading vehicles"} />}
                    {loading === false || loading === null
                        ? <DropDownMenu
                            menuName={'vehicle'}
                            required={true}
                            testId={'vehicle-dropdown'}
                            defaultVal={carInfo.make && filteredVehicles
                                ? {
                                    label: selectedVehDetails.license_plate,
                                    value: selectedVehDetails.id,
                                    enabled: true
                                }
                                : undefined
                            }
                            choices={filteredVehicles
                                ? filteredVehicles.map((item: IVehicle) => ({
                                    label: item.license_plate + ' - ' + item.make + ' ' + item.model,
                                    value: item.id,
                                    enabled: true
                                }))
                                : []
                            }
                            onSelect={(event: ChangeEvent<HTMLSelectElement>) => onSelectVeh(event)}
                        />
                        : <div style={{ height: '5vh' }}><Elliptical /></div>
                    }
                </div>
                <p style={{ margin: '2.5vh 0' }}>
                    If a vehicle is not available for assignment in the dropdown please raise with your Ops Lead.
                </p>
                <VehicleDetails
                    make={selectedVehDetails.make}
                    model={selectedVehDetails.model}
                    year={selectedVehDetails.year}
                    color={selectedVehDetails.color}
                    licensePlate={selectedVehDetails.license_plate}
                    vin={selectedVehDetails.vin}
                    bodyType={selectedVehDetails.specs.body_type}
                    startingMiles={selectedVehDetails.current_meter_value}
                />
                <div style={{ margin: '2.5vh 0' }}>
                    <p>Starting {countrySpecificDistanceUnit(country)}</p>
                    <input
                        style={{
                            borderRadius: '10px',
                            border: '1px solid #C4C4C4',
                            height: '5vh',
                            textAlign: 'left',
                            width: '50%',
                        }}
                        type="text"
                        name='odometer'
                        value={showOdometer ? selectedVehDetails.current_meter_value : ""}
                        onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            const inputVal = e.target.value;
                            const numericVal = inputVal.replace(/[^0-9]/g, '');
                            setSelectedVehDetails(prevObj => ({ ...prevObj, current_meter_value: Number(numericVal) }));
                            updateFormState(UserFormStates.carInfo, { odometer: Number(numericVal) });
                        }}
                    />
                </div>
                <p style={{ margin: '2.5vh 0' }}>Please double check the odometer in the vehicle as part of the onboarding process.</p>
                <div style={{ margin: '2.5vh 0' }}>
                    {odometerCheck && <WarningBox message={"The odometer value provided is less than the recorded value in Fleetio. You can still proceed with the assignment; however, you will need to manually update the value in both Geotab and Fleetio after the onboard"} />}
                </div>
                <SubmitBtn text={"Next"} breadcrumbObject={breadcrumbObject} componentName={componentName} />
            </form>
        </>
    );
}
